import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, User, Calendar, Clock, Euro, FileText, CheckCircle, Phone, Monitor, MapPin, MessageCircle, X } from 'lucide-react';
import { Card, Button } from '../components';
import { useVacancyApplications, useRespondToApplication, VacancyApplication } from '../hooks/useVacancies';
import api from '../services/api';
import { useAuthStore } from '../store/authStore';
import { uploadUrl } from '../utils/uploadUrl';
import { ComplianceTierMedaille, isMedailleTier } from '../components/ComplianceTierMedaille';

/** Anonieme profielkaart tot acceptatie: 'Kandidaat #n' i.p.v. de naam. */
const kandidaatNaam = (app: VacancyApplication) =>
  app.anoniem ? `Kandidaat #${app.kandidaat_nummer ?? ''}` : `${app.first_name ?? ''} ${app.last_name ?? ''}`.trim();

const UITNODIGING_TYPES = [
  { value: 'telefoon', label: 'Telefonisch contact', icon: Phone },
  { value: 'teams', label: 'Gesprek via Teams', icon: Monitor },
  { value: 'locatie', label: 'Gesprek op locatie', icon: MapPin },
  { value: 'chat', label: 'Chat op BeLagom', icon: MessageCircle },
];

const RespondModal: React.FC<{
  app: VacancyApplication;
  vacancyId: string;
  onClose: () => void;
}> = ({ app, vacancyId, onClose }) => {
  const [action, setAction] = useState<'accepteer' | 'uitnodiging' | 'afwijzen' | null>(null);
  const [uitnodigingType, setUitnodigingType] = useState('');
  const [message, setMessage] = useState('');
  const respond = useRespondToApplication(vacancyId);

  const handleSubmit = () => {
    if (!action) return;
    respond.mutate(
      { appId: app.id, action, uitnodiging_type: uitnodigingType || undefined, message: message || undefined },
      { onSuccess: onClose }
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-base font-semibold text-gray-900">
            Reageren op {kandidaatNaam(app)}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Action */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-3">Kies een actie</p>
            <div className="space-y-2">
              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-gray-200 hover:border-green-400 hover:bg-green-50 transition-colors">
                <input type="radio" name="action" value="accepteer" checked={action === 'accepteer'} onChange={() => setAction('accepteer')} className="mt-0.5" />
                <div>
                  <span className="text-sm font-medium text-gray-800">Per direct beginnen</span>
                  <p className="text-xs text-gray-500 mt-0.5">Samenwerking start direct. Medewerkers kunnen worden aangemeld.</p>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-colors">
                <input type="radio" name="action" value="uitnodiging" checked={action === 'uitnodiging'} onChange={() => setAction('uitnodiging')} className="mt-0.5" />
                <div>
                  <span className="text-sm font-medium text-gray-800">Uitnodiging versturen</span>
                  <p className="text-xs text-gray-500 mt-0.5">Plan eerst een gesprek voordat je besluit.</p>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-gray-200 hover:border-red-300 hover:bg-red-50 transition-colors">
                <input type="radio" name="action" value="afwijzen" checked={action === 'afwijzen'} onChange={() => setAction('afwijzen')} className="mt-0.5" />
                <div>
                  <span className="text-sm font-medium text-gray-800">Afwijzen</span>
                  <p className="text-xs text-gray-500 mt-0.5">Kandidaat afwijzen voor deze vacature.</p>
                </div>
              </label>
            </div>
          </div>

          {/* Uitnodiging type */}
          {action === 'uitnodiging' && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Soort gesprek</p>
              <div className="grid grid-cols-2 gap-2">
                {UITNODIGING_TYPES.map(({ value, label, icon: Icon }) => (
                  <label key={value} className={`flex items-center gap-2 cursor-pointer p-2.5 rounded-lg border text-sm transition-colors ${
                    uitnodigingType === value ? 'border-blue-500 bg-blue-50 text-blue-800' : 'border-gray-200 hover:border-blue-300'
                  }`}>
                    <input type="radio" name="uitnodiging_type" value={value} checked={uitnodigingType === value} onChange={() => setUitnodigingType(value)} className="sr-only" />
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Message */}
          {action && action !== 'afwijzen' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bericht <span className="text-gray-400 font-normal">(optioneel)</span>
              </label>
              <textarea
                rows={3}
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Eventuele toelichting voor de professional..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={respond.isPending}>Annuleren</Button>
          <Button
            onClick={handleSubmit}
            disabled={!action || (action === 'uitnodiging' && !uitnodigingType) || respond.isPending}
            loading={respond.isPending}
          >
            Versturen
          </Button>
        </div>
      </div>
    </div>
  );
};

export const EmployerVacancyApplicationsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [selectedApp, setSelectedApp] = useState<VacancyApplication | null>(null);

  const { data: vacancy } = useQuery({
    queryKey: ['employer-vacancy', id],
    queryFn: async () => {
      const res = await api.get(`/employer/vacancy/${id}`);
      return res.data?.data || res.data || null;
    },
    enabled: !!id && user?.role === 'opdrachtgever',
  });

  const { data: applications = [], isLoading } = useVacancyApplications(id);

  const statusBadge = (status: string) => {
    const map: Record<string, string> = {
      ingediend: 'bg-blue-100 text-blue-800',
      geaccepteerd: 'bg-green-100 text-green-800',
      afgewezen: 'bg-red-100 text-red-800',
    };
    return map[status] || 'bg-gray-100 text-gray-800';
  };

  const statusLabel = (app: VacancyApplication) => {
    if (app.status === 'ingediend') return 'Nieuw';
    if (app.status === 'geaccepteerd') return 'Geaccepteerd';
    if (app.status === 'afgewezen') return 'Afgewezen';
    return app.status;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Terug
        </Button>
      </div>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Sollicitaties</h1>
        {vacancy && <p className="mt-1 text-gray-600">{vacancy.titel}</p>}
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto" />
        </div>
      ) : applications.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <User className="mx-auto h-12 w-12 text-gray-400 mb-3" />
            <p className="text-gray-600">Nog geen sollicitaties ontvangen.</p>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {applications.map((app) => (
            <Card key={app.id} className="p-6">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="flex-1">
                  {/* Header */}
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      {app.profile_photo_url ? (
                        <img src={uploadUrl(app.profile_photo_url)} className="w-10 h-10 rounded-full object-cover" alt="" />
                      ) : (
                        <User className="w-5 h-5 text-blue-600" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-base font-semibold text-gray-900">
                          {kandidaatNaam(app)}
                        </h3>
                        {app.company_name && (
                          <span className="text-sm text-gray-500">— {app.company_name}</span>
                        )}
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(app.status)}`}>
                          {statusLabel(app)}
                        </span>
                      </div>
                      {app.email ? (
                        <p className="text-sm text-gray-500">{app.email}</p>
                      ) : (
                        <p className="text-xs text-gray-400">Naam en contactgegevens zichtbaar na acceptatie</p>
                      )}
                    </div>
                    {isMedailleTier(app.compliance_tier) && app.compliance_indicatoren && (
                      <ComplianceTierMedaille tier={app.compliance_tier} indicatoren={app.compliance_indicatoren} size={44} className="ml-auto flex-shrink-0" />
                    )}
                  </div>

                  {/* Meta row */}
                  <div className="mt-3 flex flex-wrap gap-4 text-sm text-gray-600">
                    {app.available_from && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        Beschikbaar {new Date(app.available_from).toLocaleDateString('nl-NL')}
                      </span>
                    )}
                    {app.hours_per_week && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {app.hours_per_week} uur/week
                      </span>
                    )}
                    {app.rate_proposal && (
                      <span className="flex items-center gap-1">
                        <Euro className="w-3.5 h-3.5" />
                        €{app.rate_proposal}/uur
                      </span>
                    )}
                    {app.cv_attachment_url && (
                      <a
                        href={uploadUrl(app.cv_attachment_url)}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-1 text-blue-600 hover:underline"
                      >
                        <FileText className="w-3.5 h-3.5" />
                        CV bekijken
                      </a>
                    )}
                  </div>

                  {/* Certifications */}
                  {Array.isArray(app.certifications) && app.certifications.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {app.certifications.map(c => (
                        <span key={c} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">{c}</span>
                      ))}
                    </div>
                  )}

                  {/* Motivation */}
                  {app.motivation && (
                    <div className="mt-3">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Motivatie</p>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{app.motivation}</p>
                    </div>
                  )}

                  {/* Relevant experience */}
                  {app.relevant_experience && (
                    <div className="mt-3">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Relevante ervaring</p>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{app.relevant_experience}</p>
                    </div>
                  )}

                  {/* Confidentiality */}
                  {app.confidentiality_declared && (
                    <div className="mt-2 flex items-center gap-1.5 text-xs text-green-700">
                      <CheckCircle className="w-3.5 h-3.5" />
                      Vertrouwelijkheidsverklaring ondertekend
                    </div>
                  )}

                  {/* Employer response (if already responded) */}
                  {app.employer_response && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm">
                      <span className="font-medium text-gray-700">Jouw reactie: </span>
                      <span className="text-gray-600">
                        {app.employer_response === 'accepteer' && 'Per direct gestart'}
                        {app.employer_response === 'uitnodiging' && `Uitnodiging (${app.uitnodiging_type})`}
                        {app.employer_response === 'afwijzen' && 'Afgewezen'}
                      </span>
                      {app.employer_response_message && (
                        <p className="mt-1 text-gray-600 italic">"{app.employer_response_message}"</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                {app.status === 'ingediend' && (
                  <div className="flex-shrink-0">
                    <Button onClick={() => setSelectedApp(app)}>
                      Reageren
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {selectedApp && id && (
        <RespondModal
          app={selectedApp}
          vacancyId={id}
          onClose={() => setSelectedApp(null)}
        />
      )}
    </div>
  );
};
